<template>
  <div class="not-found-view min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8 text-center">
      <!-- 404 Illustration -->
      <div class="mx-auto">
        <svg
          class="w-32 h-32 mx-auto text-gray-400 dark:text-gray-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1"
            d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </div>

      <!-- Error Code -->
      <div>
        <h1 class="text-6xl font-bold text-gray-900 dark:text-gray-100 mb-4">404</h1>
        <h2 class="text-2xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Page Not Found</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-8">
          The page you're looking for doesn't exist or has been moved.
        </p>
      </div>

      <!-- Action Buttons -->
      <div class="space-y-4">
        <div class="flex flex-col sm:flex-row gap-3 justify-center">
          <RouterLink to="/" class="btn-primary inline-flex items-center justify-center">
            <svg
              class="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            Go Home
          </RouterLink>

          <button class="btn-secondary inline-flex items-center justify-center" @click="goBack">
            <svg
              class="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Go Back
          </button>
        </div>

        <!-- Popular Links -->
        <div class="mt-8">
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Or try one of these popular pages:
          </p>
          <div class="space-y-2">
            <RouterLink
              to="/tools"
              class="block text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 text-sm transition-colors duration-200"
            >
              Browse Tools
            </RouterLink>
            <RouterLink
              to="/profile"
              class="block text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 text-sm transition-colors duration-200"
            >
              Profile Settings
            </RouterLink>
            <a
              href="mailto:support@profikid.nl"
              class="block text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 text-sm transition-colors duration-200"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>

      <!-- Search Box -->
      <div class="mt-8">
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search for tools or features..."
            class="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
            @keyup.enter="performSearch"
          />
          <svg
            class="absolute left-3 top-3.5 w-5 h-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <button
          :disabled="!searchQuery.trim()"
          class="mt-3 w-full btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          @click="performSearch"
        >
          Search
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const searchQuery = ref('');

// Methods
const goBack = () => {
  if (window.history.length > 1) {
    router.go(-1);
  } else {
    router.push('/');
  }
};

const performSearch = () => {
  if (searchQuery.value.trim()) {
    router.push({
      name: 'tools',
      query: { search: searchQuery.value.trim() },
    });
  }
};

// Set page title
document.title = '404 - Page Not Found | MCP Tools Collection';

// Track 404 error (optional analytics)
if (typeof window !== 'undefined' && window.gtag) {
  window.gtag('event', 'page_view', {
    page_title: '404 - Page Not Found',
    page_location: window.location.href,
  });
}
</script>

<style scoped>
.not-found-view {
  background: linear-gradient(
    135deg,
    theme('colors.gray.50') 0%,
    theme('colors.blue.50') 50%,
    theme('colors.purple.50') 100%
  );
}

.dark .not-found-view {
  background: linear-gradient(
    135deg,
    theme('colors.gray.900') 0%,
    theme('colors.gray.800') 50%,
    theme('colors.gray.900') 100%
  );
}

/* Animation for the 404 text */
@keyframes bounce-in {
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  50% {
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

h1 {
  animation: bounce-in 0.6s ease-out;
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  h1 {
    animation: none;
  }
}

/* Print styles */
@media print {
  .not-found-view {
    background: white;
    color: black;
  }

  button,
  .btn-primary,
  .btn-secondary {
    display: none;
  }
}
</style>
